import { useContext, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import {
	makeStyles,
	Grid,
	Typography,
	IconButton,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
} from '@material-ui/core';
import { replaceDataSpaceDataset } from '../../../../api_helper/api';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import CreateEditDataSpaceContext from '../../context/CreateEditDataSpaceContext';
import NBALogoPNG from '../../../../assets/img/NBALogoSquare.png';
import MLBLogoPNG from '../../../../assets/img/MLBLogoSquare.png';
import NFLLogoPNG from '../../../../assets/img/NFLLogoSquare.png';
import NHLLogoPNG from '../../../../assets/img/NHLLogoSquare.png';
import YahooFantasyLogoPNG from '../../../../assets/img/YahooFantasyLogo.png';
import { ObjectId } from 'bson';
import SnackbarAlertContext from '../../../../context/SnackbarAlertContext';

const useStyles = makeStyles((theme) => ({
	dialogWrapper: {
		width: '100%',
		padding: '1.5rem',
		paddingBottom: '4.5rem',
		display: 'flex',
		flexDirection: 'column',
	},
	dialogHeaderWrapper: {
		width: '100%',
		marginBottom: '2rem',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	dialogHeaderText: {
		fontSize: '1.5rem',
		fontWeight: 'bold',
		marginLeft: 'auto',
	},
	dialogExitButton: {
		marginLeft: 'auto',
	},
	datasetWrapper: {
		display: 'flex',
	},
	datasetLogo: {
		height: '100px',
		marginRight: '0.8rem',
		borderRadius: '0.5rem',
	},
	datasetInfoWrapper: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	datasetField: {
		width: '100%',
		height: 'fit-content',
		padding: '0.3rem',
		backgroundColor: '#C4C4C4',
		borderRadius: '0.2rem',
		color: '#000000',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		'&:hover': {
			backgroundColor: '#A2A2A2',
		},
	},
	datasetFieldSelected: {
		backgroundColor: '#0DBC42',
		'&:hover': {
			backgroundColor: '#01982E',
		},
	},
	nextButton: {
		alignSelf: 'end',
	},
}));

// Dialog (modal) allowing user to edit the selected fields of a selected datasource.
const EditSelectedDataSourceFieldsDialog = (props) => {
	const classes = useStyles();
	const createEditDataSpaceContext = useContext(CreateEditDataSpaceContext);
	const snackbarAlertContext = useContext(SnackbarAlertContext);

	const [dataset, setDataset] = useState(props.dataset);

	const getDatasetLogoSource = () => {
		switch (props.dataset.tags.api) {
			case 'Yahoo! Fantasy Sports':
				return YahooFantasyLogoPNG;
			case 'DataFeeds':
			case 'DataFeedsGraphQL':
			default:
				switch (props.dataset?.tags?.sport) {
					case 'NBA':
						return NBALogoPNG;
					case 'MLB':
						return MLBLogoPNG;
					case 'NFL':
						return NFLLogoPNG;
					case 'NHL':
						return NHLLogoPNG;
					default:
						return NBALogoPNG;
				}
		}
	};

	const saveButtonClickHandler = async () => {
		if (dataset._id !== props.dataset._id) {
			// Create dataset instance.
			const datasetInstance = {
				...dataset,
				_id: new ObjectId().toString(),
				originDatasetId: dataset._id,
			};

			// Add Fantrax fields (if applicable)
			if (dataset.tags.api === 'Fantrax') {
				datasetInstance.userId = createEditDataSpaceContext.newDataSpace.fantasyLeague?.userId;
				datasetInstance.leagueName = createEditDataSpaceContext.newDataSpace.fantasyLeague?.name;
				datasetInstance.leagueKey =
					createEditDataSpaceContext.newDataSpace.fantasyLeague?.league_key ||
					createEditDataSpaceContext.newDataSpace.fantasyLeague?.leagueId;
			}

			// Add Yahoo! Fantasy Sports fields (if applicable)
			if (dataset.tags.api === 'Yahoo! Fantasy Sports') {
				datasetInstance.userId = createEditDataSpaceContext.newDataSpace.fantasyLeague?.userId;
				datasetInstance.leagueName = createEditDataSpaceContext.newDataSpace.fantasyLeague?.name;
				datasetInstance.leagueKey =
					createEditDataSpaceContext.newDataSpace.fantasyLeague?.league_key ||
					createEditDataSpaceContext.newDataSpace.fantasyLeague?.leagueId;
				datasetInstance.gameKey = createEditDataSpaceContext.newDataSpace.fantasyLeague?.league_key?.substring(0, 3);
			}
		}

		createEditDataSpaceContext.setNewDataSpace((prevState) => {
			const updatedSelectedDatasets = prevState.selectedDatasets.map((selectedDataset) => {
				if (selectedDataset._id === props.dataset._id) {
					return dataset;
				} else {
					return selectedDataset;
				}
			});

			return {
				...prevState,
				selectedDatasets: updatedSelectedDatasets,
			};
		});

		props.setIsEditSelectedDatasetFieldsDialogOpen(false);
	};

	const datasetFieldClickHandler = (e, clickedField) => {
		setDataset((prevState) => {
			const updatedFields = prevState.fields.map((field) => {
				if (field.name === clickedField.name) {
					field.isSelected = !field.isSelected;
				}

				return field;
			});

			return {
				...prevState,
				fields: updatedFields,
			};
		});
	};

	const datasetFieldComponents = dataset.fields.map((field) => (
		<Grid key={props.dataset._id} item xs={12} sm={6}>
			<Button
				className={classes.datasetField + (field.isSelected ? ' ' + classes.datasetFieldSelected : '')}
				onClick={(e) => {
					datasetFieldClickHandler(e, field);
				}}
			>
				{field.displayName}
			</Button>
		</Grid>
	));

	const dialogCloseButtonClickHandler = () => {
		props.setIsEditSelectedDatasetFieldsDialogOpen(false);
	};

	const timeFrameDatasets = createEditDataSpaceContext.newDataSpace.allDatasets.filter(
		(dataset) => dataset.name === props.dataset.name,
	);

	useEffect(() => {
		setDataset(props.dataset);
	}, [props.dataset]);

	const changeDatasetHandler = (e) => {
		setDataset(e.target.value);
	};

	return (
		<Dialog fullWidth open={props.isEditSelectedDatasetFieldsDialogOpen}>
			<div className={classes.dialogWrapper}>
				<div className={classes.dialogHeaderWrapper}>
					<Typography className={classes.dialogHeaderText} variant="h2">
						Select Your Fields
					</Typography>
					<IconButton className={classes.dialogExitButton} size="medium" onClick={dialogCloseButtonClickHandler}>
						<DisabledByDefaultIcon fontSize="inherit" />
					</IconButton>
				</div>

				<div style={{ marginBottom: '0.5rem', display: 'flex' }}>
					<img src={getDatasetLogoSource()} alt="Dataset logo." className={classes.datasetLogo} />

					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<Typography variant="h6">{props.dataset.displayName}</Typography>
						<Typography>{props.dataset.season ? ` (${props.dataset.season})` : ''}</Typography>
						<Typography>
							Select the fields from this dataset you would like to include in you dataspace. You can always add more
							from within your dataspace.
						</Typography>
					</div>
				</div>

				{dataset.tags.timeFrame && (
					<FormControl fullWidth style={{ marginBottom: '0.5rem' }}>
						<InputLabel id="season-select-input-label">Season</InputLabel>
						<Select
							labelId="season-select-label"
							id="season-select-input"
							value={dataset}
							renderValue={(dataset) => dataset.tags.timeFrame}
							label={'Season'}
							onChange={changeDatasetHandler}
						>
							{timeFrameDatasets.map((timeFrameDataset) => (
								<MenuItem key={timeFrameDataset._id} value={timeFrameDataset}>
									{timeFrameDataset.tags.timeFrame}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				<Grid container spacing={0.5}>
					{datasetFieldComponents}
				</Grid>

				<Button className={classes.nextButton} onClick={saveButtonClickHandler}>
					Save
				</Button>
			</div>
		</Dialog>
	);
};

export default EditSelectedDataSourceFieldsDialog;
